import { useQSSKey } from "@contexts/QueryStringStorageContext";
import { useQueryStringStorage } from "@hooks/useQueryStringStorage/useQueryStringStorage";
import analyticstracker from "@jmc/analyticstracker";
import { EventTypes } from "@jmc/core/src/types/EventTypes";
import { Tag } from "@jmc/solid-design-system/src/components/atoms/Tag/Tag";
import { Typography } from "@jmc/solid-design-system/src/components/atoms/Typography/Typography";
import {
    CurrentRefinementsConnectorParamsItem,
    CurrentRefinementsConnectorParamsRefinement,
} from "instantsearch.js/es/connectors/current-refinements/connectCurrentRefinements";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    useClearRefinements,
    useCurrentRefinements,
    UseCurrentRefinementsProps,
    useHits,
    useSearchBox,
} from "react-instantsearch";

import style from "./style.module.scss";

interface CustomCurrentRefinementsProps extends UseCurrentRefinementsProps {
    getTranslation?: (item: string, t: (item: string) => string) => string;
}

export const CustomCurrentRefinements = (props: CustomCurrentRefinementsProps): JSX.Element => {
    const { items, refine } = useCurrentRefinements(props);
    const { query } = useSearchBox();
    const { hits } = useHits(props);
    const [resultCount, setResultCount] = useState([]);
    const [queryFilterResultCount, setQueryFilterResultCount] = useState([]);

    // result count based on filter
    useEffect(() => {
        if (hits?.length > 0 && !resultCount?.length) {
            setResultCount(hits);
        }
    }, [hits, resultCount]);

    // result count based on query search
    useEffect(() => {
        if (query && hits?.length > 0 && !items?.length) {
            setQueryFilterResultCount(hits);
        }
    }, [hits]);

    const clearRefinements = useClearRefinements(props);
    const { getTranslation = (item: string) => item } = props;
    const qss = useQueryStringStorage();
    const hashKey = useQSSKey();

    const { t } = useTranslation();

    if (items.length === 0) {
        return null;
    }

    const refinementTags: CurrentRefinementsConnectorParamsRefinement[] = [];

    items.forEach((item: CurrentRefinementsConnectorParamsItem) => {
        item.refinements.forEach((refinement) => {
            refinementTags.push(refinement);
        });
    });

    const eventTracking = (): void => {
        analyticstracker().trackEvent({
            event: EventTypes.SEARCH_RESULT,
            info: {
                keyword: query || "unset",
                type: "recipe",
                resultcategory: "unset",
                resultcount: query ? queryFilterResultCount?.length : resultCount?.length,
                filter: "reset filter",
            },
        });
    };

    const customRefinementTags = refinementTags.map(
        (refinement: CurrentRefinementsConnectorParamsRefinement, index: number) => {
            return (
                <Tag
                    color="primary"
                    dismissable={true}
                    key={index}
                    onClick={() => {
                        qss.handleQueryParam(hashKey, refinement.attribute, refinement.value);
                        refine(refinement);
                        eventTracking();
                    }}
                >
                    <Typography size="xs" weight="400" color="white">
                        {getTranslation(refinement?.label, t)}
                    </Typography>
                </Tag>
            );
        },
    );

    return (
        <>
            <div className={style["refinement-tags"]}>
                <fieldset className={style.fieldset} aria-labelledby={t("Selected filters", { ns: "common" })}>
                    {customRefinementTags}
                </fieldset>
            </div>
            {customRefinementTags.length > 0 && (
                <button
                    className={style["reset-filters-link"]}
                    onClick={() => {
                        qss.clearItems(hashKey);
                        clearRefinements?.refine();
                        eventTracking();
                    }}
                >
                    <Typography variant="body">{t("Reset filters", { ns: "recipe" })}</Typography>
                </button>
            )}
        </>
    );
};

export default CustomCurrentRefinements;
